import type { LocaleMessageSchema } from '../types';

export default {
  Common: {
    loading: 'Chargement...',
  },
  errors: {
    session_create_error:
      'Échec de la création de la session.\nVeuillez réessayer.',
    session_continue_error:
      'Impossible de charger votre session. Veuillez réessayer. Si le problème persiste, contactez le support à {supportEmail}.',
    kiosk_not_found: 'Échec du chargement du kiosque.\nVeuillez réessayer.',

    art_style_missing: "Aucun style artistique n'a été ajouté à ce kiosque",
    art_style_fetch_error: 'Échec du chargement du style artistique',
    art_style_preload_error:
      "Échec du chargement de l'aperçu du style artistique",

    face_detection_server_error:
      'Échec du traitement de la photo. Veuillez réessayer.',
    face_detection_preload_error:
      'Erreur lors du préchargement des visages détectés',
    face_detection_no_faces:
      'Aucun visage détecté. Veuillez essayer de prendre une autre photo.',

    photo_webcam_not_allowed: 'Autorisation de la caméra refusée',
    photo_webcam_not_found:
      'Caméra introuvable. Veuillez vérifier si elle est connectée au kiosque.',
    photo_webcam_hardware_error:
      "La caméra est occupée ou ne fonctionne pas.\nVeuillez réessayer ou demander de l'aide.",
    photo_webcam_photo_failed:
      'Impossible de prendre une photo\navec la caméra',

    photo_phone_missing_session_id:
      "Nous n'avons pas trouvé votre session.\nVeuillez scanner à nouveau le code QR.",
    photo_phone_missing_kiosk_slug:
      'Kiosque introuvable dans votre session. Si le problème persiste, contactez le support à {supportEmail}.',
    photo_phone_limit_exceeded: 'Trop de photos sélectionnées',
    photo_phone_qr_error:
      'Échec de la génération du code QR\npour le téléchargement de photos',

    photo_file_unsupported_type: 'Type de fichier non pris en charge',
    photo_file_cannot_process:
      'Impossible de traiter cette photo.\nVeuillez en essayer une autre.',
    photo_file_cannot_process_heic:
      'Impossible de traiter la photo HEIC.\nVeuillez en essayer une autre.',

    portrait_generation_error:
      'Une erreur est survenue lors de la génération de votre portrait. Veuillez réessayer.',
    portrait_generation_faces_not_selected:
      'Aucun visage sélectionné pour\ngénérer le portrait',
    portrait_generation_style_not_selected:
      'Aucun style artistique sélectionné pour\ngénérer le portrait',
    portrait_generation_over_limit: 'Trop de tentatives de génération',

    portraits_download_session_error:
      "Nous n'avons pas trouvé votre session. Veuillez vérifier le code saisi. Si le problème persiste, contactez le support à {supportEmail}.",
    portraits_download_kiosk_error:
      'Kiosque introuvable dans votre session. Si le problème persiste, contactez le support à {supportEmail}.',
    portraits_download_portraits_error:
      'Aucun portrait trouvé dans votre session. Si le problème persiste, contactez le support à {supportEmail}.',

    payment_partner_mode_missing_stripe_account:
      'Le mode de paiement partenaire nécessite un compte Stripe Connect. Veuillez configurer le compte Stripe du partenaire ou passer en mode de paiement plateforme.',

    api_error_missing_session_id: 'Session introuvable',
    api_error_email_code_validation:
      'Échec de la validation du code de paiement. Veuillez réessayer.',
  },

  /**
   * Global components
   */

  OverlayError: {
    buttons: {
      close: 'Fermer',
      restart: 'Recommencer',
      retry: 'Réessayer',
    },
  },
  Logo: {
    startOver: 'Recommencer',
  },
  Copyright: {
    copyright: 'powered by TheArtisan.ai',
    distribution: 'distribué par',
    terms:
      'En prenant une photo sur cet appareil, vous acceptez nos politiques et conditions. Pour les consulter, veuillez',
    termsLink: 'cliquer ici',
  },
  IdlePopup: {
    title: 'Vous êtes toujours là ?',
    caption: 'Votre session se terminera si vous êtes inactif.',
    buttonContinue: 'Oui, je suis toujours là',
    buttonRestart: 'Non, recommencer',
  },
  GDPRConsent: {
    title: 'Consentement requis',
    consentText:
      'Nous avons besoin de votre consentement pour traiter vos caractéristiques faciales (données biométriques) afin de créer des transformations artistiques. Vous ne pouvez continuer que si vous avez 16 ans ou plus (ou avec le consentement parental).',
    buttonConsent: "J'accepte",
    buttonDecline: 'Je refuse',
    privacyPolicyLink: 'Voir la politique de confidentialité',
    declineMessage: 'Vous ne pouvez pas continuer sans consentement',
  },
  StepProgressIndicator: {
    photoShort: 'Photo',
    photoLong: 'Prendre une photo',
    facesShort: 'Visages',
    facesLong: 'Choisir les visages',
    groupPortraitsShort: 'Groupe',
    groupPortraitsLong: 'Portraits de groupe',
    portraitsShort: 'Portraits',
    portraitsLong: 'Choisir les portraits',
    printShort: 'Imprimer',
    printLong: 'Payer & Imprimer',
    sendToPickupShort: 'Retrait',
    sendToPickupLong: 'Vers le retrait',
  },
  UploadedPhotosSidebar: {
    title: 'Vos photos',
    count: '+{extraPhotoCount} de plus',
  },
  NumericKeyboard: {
    invalidPin: 'Code PIN invalide',
    clear: 'Effacer',
    enter: 'Entrer',
  },
  OutOfService: {
    message:
      "Votre contrat semble être en pause ou expiré.\nVeuillez contacter votre distributeur pour obtenir de l'aide.",
  },

  /**
   * Steps
   */

  Home: {
    title: 'Générez votre portrait en quelques secondes !',
    button: 'Essayez maintenant !',
  },
  ArtStyle: {
    title: 'Choisissez votre style',
  },
  PhotoFromWebcam: {
    titleGetReady: 'Préparez-vous à prendre une photo',
    titleAttention: 'Regardez la caméra !',
    titleReview: 'Aimez-vous cette photo ?',
    titleUpload: 'Téléchargement de votre photo…',
    buttonPrimaryPhoto: 'Prendre une photo',
    buttonPrimaryUse: 'Oui, utiliser cette photo',
    buttonPrimaryRetake: 'Reprendre la photo',
    buttonSecondaryUpload: 'Télécharger depuis le téléphone',
    buttonSecondaryRetake: 'Non, reprendre (1 essai restant)',
    buttonSecondaryUseFirst: 'Non, utiliser la première photo',
    titleCameraError: 'Caméra indisponible',
    buttonRetryCamera: 'Réessayer la caméra',
  },
  PhotoPromptFromPhone: {
    title: 'Télécharger des photos depuis le téléphone ?',
    caption:
      'Surprenez vos amis et votre famille —\ntéléchargez leurs photos pour créer des portraits.',
    buttonUpload: 'Oui, depuis le téléphone',
    buttonSkip: 'Non, ignorer',
  },
  PhotoFromPhone: {
    title: 'Téléchargez des photos depuis votre téléphone',
    step1: 'Scannez le code QR avec votre téléphone',
    step2: 'Sélectionnez les photos',
    step3: 'Attendez le téléchargement',
    waiting: 'En attente de votre téléchargement…',
    done: 'Téléchargement réussi',
    buttonCancel: 'Annuler & Retour',
    buttonSkip: 'Ignorer le téléchargement',
    buttonContinue: 'Continuer',
    gdprConsentAll:
      "J'ai le consentement de toutes les personnes sur cette photo pour traiter leurs caractéristiques faciales",
    gdprOver16: "Personne sur cette photo n'a moins de 16 ans",
    gdprResponsibility:
      "J'accepte l'entière responsabilité de la conformité RGPD",
    gdprAgreeToPrefix: "J'accepte les",
    gdprPrivacyPolicyLink: 'politique de confidentialité',
    gdprAgreeToSuffix: 'conditions pour les photos téléchargées',
    gdprCheckboxesRequired:
      'Veuillez cocher toutes les cases ci-dessous pour continuer',
    buttonSkipKioskPhoto: 'Ignorer la photo du kiosque',
    buttonTakeWebcamPhoto: 'Prendre une photo avec la webcam',
  },
  FacesIndividual: {
    title: 'Pour qui souhaitez-vous créer des portraits individuels ?',
    buttonConfirm: 'Confirmer & Continuer',
    genderMale: 'Masculin',
    genderFemale: 'Féminin',
    genderNonBinary: 'Non-binaire',
    age0to2: '0-2',
    age3to9: '3-9',
    age10to19: '10-19',
    age20to29: '20-29',
    age30to39: '30-39',
    age40to49: '40-49',
    age50to59: '50-59',
    age60to69: '60-69',
    age70plus: '70+',
    raceWhite: 'Blanc',
    raceBlack: 'Noir',
    raceEastAsian: "Asiatique de l'Est",
    raceSoutheastAsian: 'Asiatique du Sud-Est',
    raceIndian: 'Indien',
    raceLatino: 'Latino',
    raceMena: 'Moyen-Oriental',
  },
  Portraits: {
    title: 'Choisissez vos portraits !',
    buttonNext: 'Suivant : Imprimer la sélection',
    buttonNextGroupOnly: 'Continuer avec les portraits de groupe uniquement',
    galleryScrollBack: 'Portraits précédents',
    galleryScrollForward: 'Portraits suivants',
    filterLabel: 'Afficher',
    filterAll: 'Tous',
    filterGroup: 'Ensemble',
    filterIndividual: 'Individuels',
    stageKindGroup: 'Portrait de groupe',
    stageKindIndividual: 'Portrait',
    stagePosition: '{index} sur {count}',
    stageSelect: 'Choisir',
    stageSelected: 'Choisi ✓',
    buttonMore: 'Créer plus de portraits ({count} restants)',
    popupTitle: 'Voulez-vous finaliser votre commande ?',
    popupGroupSelected: 'Portraits de groupe sélectionnés : {count} ({price})',
    popupIndividualSelected:
      'Portraits individuels sélectionnés : {count} ({price})',
    popupTotalSum: 'Montant total : {sum}',
    popupButtonNo: 'Non, revenir',
    popupButtonYes: 'Oui, imprimer & payer !',
    error: 'Oups ! Impossible de générer ce portrait.',
    placeholderGenerating: 'Génération de votre portrait…',
    placeholderLoading: 'Chargement de votre portrait…',
    groupPlaceholder: 'Votre portrait de groupe apparaîtra ici',
    buttonRetry: 'Réessayer',
    buttonErrorDetails: "Détails de l'erreur",
    retrying: 'Nouvelle tentative...',
    maxRetriesReached: 'Nombre max. de tentatives atteint',
    retryAttempts: 'Tentatives : {count}',
    printingLockedPopupTitle:
      'Veuillez appeler le membre du personnel le plus proche pour imprimer vos portraits.',
    generationPendingTitle:
      'Certains portraits sont encore en cours de création',
    generationPendingCaption:
      '{count} portraits sont encore en cours de création. Voulez-vous attendre pour tous les voir ?',
    generationPendingWait: 'Attendre tous les portraits',
    generationPendingSendReady: 'Envoyer les {count} portraits prêts',
    printingLockedPopupCaption:
      "L'impression au kiosque n'est pas disponible en l'absence d'un membre du personnel.",
  },
  PortraitsGroup: {
    buttonMore: 'Créer plus de portraits',
  },
  PrintLock: {
    lockTitle: "Verrouiller l'impression",
    unlockTitle: "Déverrouiller l'impression",
  },
  PaymentType: {
    title: 'Comment souhaitez-vous payer ?',
    optionCardTitle: 'Carte bancaire',
    optionCardCaption: 'Visa, Mastercard, & 10+ cartes prises en charge',
    optionVoucherTitle: 'Portraits Prépayés',
    optionVoucherCaption: 'Inclus dans le billet — demandez au personnel',
  },
  PaymentTerminal: {
    title: "Veuillez suivre les instructions à l'écran",
    titlePaymentSuccess: 'Impression de vos portraits…',
    titlePaymentError: 'Échec du paiement',
    titlePrintError1: "Désolé, certains portraits n'ont pas pu être imprimés",
    titlePrintError2:
      'Veuillez appuyer sur "Réimprimer" ci-dessous pour réessayer.',
    buttonRetryPrinting: 'Réimprimer',
    buttonRetry: 'Réessayer',
    buttonCancel: 'Annuler',
    buttonExitSession: 'Recommencer',
    buttonBack: 'Retour',
    total: 'Total :',
    captionPayment: 'En attente de votre paiement…',
    captionPrinting: 'Impression réussie',
    captionPaymentError:
      "Une erreur s'est produite. Veuillez réessayer ou recommencer.",
    captionPaymentFinal:
      "Le paiement n'a pas fonctionné. Veuillez demander de l'aide au personnel.",
    failureCaptions: {
      notEnoughFunds:
        'Le solde de la carte est insuffisant. Veuillez essayer une autre carte.',
      expiredCard: 'Cette carte a expiré. Veuillez essayer une autre carte.',
      cardDeclined: 'La carte a été refusée. Veuillez essayer une autre carte.',
      cardError:
        'Cette carte ne peut pas être utilisée ici. Veuillez essayer une autre carte.',
      processingError:
        "Le paiement n'a pas abouti. Veuillez réessayer avec la même carte.",
      readerError:
        "Le lecteur de carte n'est pas disponible. Veuillez demander de l'aide au personnel.",
      timeout: 'Le paiement a pris trop de temps. Veuillez réessayer.',
    },
    errorMessage: "Désolé, nous n'avons pas pu traiter votre paiement",
    popupCancelledTitle: 'Paiement annulé',
    popupCancelledCaption: 'Voulez-vous réessayer ou recommencer?',
    popupButtonContinue: 'Réessayer',
  },
  PaymentVoucher: {
    title:
      "Veuillez contacter un employé pour obtenir de l'aide pour payer avec un voucher",
    buttonBack: 'Retour',
    pinTitle: 'Payer avec un voucher',
  },
  Printing: {
    title: 'Impression de vos portraits…',
    titleError: "Désolé, certains portraits n'ont pas pu être imprimés",
    buttonPrintAgain: 'Réimprimer',
    buttonContinueWithoutPrint: 'Continuer sans imprimer',
    error: "Oups ! Impossible d'imprimer ce portrait.",
    errorPartial: '{printed} sur {total} imprimés',
    retrying: 'Nouvel essai…',
    waiting: 'En attente de l’imprimante…',
  },
  PrintFailed: {
    title: 'Désolé, nous n’avons pas pu imprimer vos photos',
    titlePartial: '{printed} photos sur {total} imprimées',
    caption:
      'Vous pouvez démarrer une nouvelle session et créer de nouveaux portraits.',
    captionRefunded: 'Nous avons remboursé {amount} sur votre carte.',
    captionNotCharged: 'Nous ne vous avons pas facturé {amount}.',
    captionRefundHelp:
      'Un membre du personnel vous aidera pour le remboursement.',
  },
  Success: {
    title: 'Merci !',
    caption:
      "Vos portraits ont été imprimés. Veuillez les récupérer à l'accueil.",
    buttonStartAgain: 'Recommencer',
  },
  GoPhotoScan: {
    title: 'Scannez votre PhotoPass',
    subtitle: 'Présentez le code QR de votre PhotoPass au scanner',
    invalidCode: 'Format de code invalide. Veuillez réessayer.',
    failedToRegister: "Échec de l'enregistrement du code. Veuillez réessayer.",
    error: 'Une erreur est survenue. Veuillez réessayer.',
  },

  /**
   * Customer-facing components
   */

  CustomerPhotoFromPhone: {
    title: 'Téléchargez des photos depuis votre galerie',
    buttonSelect: 'Sélectionner des photos',
    maxCount: '{count} photos max',
    uploading: 'Téléchargement…',
    uploadError:
      'Échec du téléchargement. Veuillez vérifier votre connexion et réessayer.',
    buttonRetry: 'Réessayer',
    successTitle: 'Téléchargement réussi',
    successCaption:
      "Veuillez retourner au kiosque et suivre les instructions à l'écran.",
  },
  CustomerPortraitsDownload: {
    title: 'Entrez le code pour obtenir votre portrait',
    buttonSubmit: 'Envoyer',
    error:
      'Ce code ne semble pas correct. Vérifiez le code à 8 caractères imprimé sur votre photo et réessayez.',
    errors: {
      wrongFormat:
        'Ce code ne semble pas correct. Vérifiez le code à 8 caractères imprimé sur votre photo et réessayez.',
      notFound:
        'Désolé, aucun portrait trouvé. Veuillez vérifier le code — il est imprimé sur le devant du portrait, en bas à gauche.',
      serverError:
        "Désolé, nous n'avons pas pu récupérer vos portraits pour le moment. Veuillez réessayer plus tard.",
    },
  },
  CustomerSessionPass: {
    tabs: {
      photos: 'Photos',
      pass: 'Pass QR',
    },
    eyebrow: 'Votre pass de session',
    title: 'Gardez cette page ouverte',
    description:
      'À la borne d’achat, scannez le code carré ci-dessous. Vos portraits sont disponibles dans l’onglet Photos.',
    descriptionNothingPrinted:
      'À la station de vente, scannez le code carré ci-dessous pour choisir et imprimer vos photos.',
    scanLabel: 'À scanner à la borne d’achat',
    codeLabel: 'Code de session',
    ready: 'Prêt à scanner',
    loading: 'Préparation du code',
    unavailable: 'Code indisponible',
    qrAlt: 'Code QR pour la borne d’achat',
    defaultVenue: 'Expérience portrait TheArtisan.ai',
  },
  CustomerPortraitsDownloadCode: {
    error: 'Oups ! Impossible de charger ce portrait.',
    placeholder: 'Chargement de votre portrait…',
    imageAlt: 'Portrait',
    buttonDownload: 'Télécharger',
    buttonSendOrShare: 'Envoyer ou partager',
    buttonRetry: 'Réessayer',
    shareTitle: 'Votre portrait de {clientName}',
    shareText: 'Offert par TheArtisan.ai',
    states: {
      processing:
        'Vos portraits sont encore en préparation. Réessayez dans un instant.',
      partial:
        'Certains portraits ou détails de la page n’ont pas pu être chargés. Les téléchargements disponibles fonctionnent toujours.',
    },
    actions: {
      enterAnotherCode: 'Saisir un autre code',
      contactSupport: 'Contacter le support',
      retryDownload: 'Réessayer le téléchargement',
      downloadFailed:
        'Le téléchargement a échoué. Vérifiez votre connexion et réessayez.',
      linkCopied: 'Lien copié. Vous pouvez le coller dans un message.',
      shareFailed:
        'Le partage est indisponible pour le moment. Veuillez réessayer.',
    },
  },
  CustomerDigitalExtras: {
    downloadTitle: 'Vos photos et extras',
    downloadIntro:
      'Enregistrez vos photos, téléchargez vos extras ou ouvrez votre billet QR.',
    photosTitle: 'Vos photos',
    photosIntro: 'Enregistrez vos photos ou ouvrez votre pass QR.',
    navigation: 'Vos téléchargements',
    tabs: {
      photos: 'Photos',
      extras: 'Vidéo',
      pass: 'Billet QR',
    },
    title: 'Vos extras numériques',
    loading: 'Chargement de vos extras…',
    loadError: 'Impossible de charger les fichiers. Vérifiez votre connexion.',
    included: 'Inclus',
    includedOrder:
      'Votre vidéo est incluse. Gardez votre billet QR pour la retrouver plus tard.',
    preparingHelp:
      'Enregistrez ce lien ou gardez votre billet QR. Vous pouvez fermer cette page et y revenir plus tard.',
    reference: 'Référence : {reference}',
    retry: 'Réessayer',
    empty: 'Aucun extra numérique n’a été ajouté à cette visite.',
    orderTitle: 'Commande vidéo',
    showCashier: 'Présentez cette commande et votre billet QR à la caisse.',
    videoTotal: 'Total vidéo',
    payAtDesk: 'Paiement à la caisse.',
    preparing: 'Préparation de vos fichiers…',
    failed:
      'Votre vidéo n’a pas pu être créée. Présentez la référence ci-dessous à l’équipe sur place pour obtenir de l’aide.',
    ready: 'Prêt à télécharger',
    video: 'Photo animée',
    videoDescription:
      'Votre moment préféré en mouvement. Pour vos stories et vos proches.',
    download: 'Télécharger',
    share: 'Partager le fichier',
    preparingShare: 'Chargement du fichier…',
    shareFailed: 'Le partage est indisponible. Téléchargez le fichier.',
  },
  GoPhotoUpload: {
    waitingForPortraits: 'Finalisation de vos portraits',
    waitingForPortraitsSubtitle:
      'Veuillez patienter pendant la création des portraits restants.',
    success: 'Photos téléchargées !',
    successSubtitle:
      'Vos photos sont maintenant disponibles sur votre PhotoPass.',
  },
  system: {
    reconnecting: 'Reconnexion au serveur…',
  },
} satisfies LocaleMessageSchema;
